import React, { useCallback, useEffect, useState } from 'react';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useDeleteConfirm } from '../../hooks/useDeleteConfirm';
import { friendlyDeleteError } from '../../utils/deleteError';
import Icon from '../Icon';
import DataTable, { Column } from '../DataTable';
import { apiGet, apiPut, apiPatch, apiDelete, planningPath } from '../../services/PlanningApiClient';
import { loadClients, restoreClient, deleteClient } from '../../services/ClientService';
import { useRole } from '../../hooks/useRole';
import { useNotifications } from '../NotificationProvider';
import { recordId, shortRef } from '../../utils/recordId';

/**
 * Trash — everything that has been archived, and the only place it can be
 * recovered from.
 *
 * The app is archive-first: deleting a vendor, guest, task, order or product
 * sets `archived` rather than removing the row. Every service implemented
 * archive/restore/delete, but only `archive` was ever wired to a button, so
 * archived records simply vanished with no way back. There were 33 of them in
 * production when this was built.
 *
 * Restore is a plain `PUT { archived: false }`, matching the convention the
 * vendor table already used. Permanent deletion is deliberately a second,
 * separate step taken from here — and the server enforces that ordering too,
 * refusing to hard-delete anything not already archived.
 */

interface TrashKind {
  key: string;
  label: string;
  /** Collection path under /planning/:project_id. */
  path: string;
  /** Key the list endpoint returns the array under. */
  listKey: string;
  /** Field to show as the record's name. */
  nameOf: (row: any) => string;
  /** Identifier used in the item URL — usually the numeric id. */
  idOf: (row: any) => string;
  /** Supports DELETE ?permanent=true. Where false, only restore is offered
   *  rather than showing a button that would fail. */
  permanent?: boolean;
  /** Restore via DELETE ?restore=true instead of PUT { archived: false }.
   *  Needed where the entity's shared Zod update schema has no `archived`
   *  field: Zod strips unknown keys, so the PUT returns 200 having changed
   *  nothing — a restore that silently does not restore. */
  restoreViaQuery?: boolean;
  /**
   * For records that do not live under /planning/:project_id/<path>?archived=true
   * (people in the workspace, clients). Replaces the generic list, restore and
   * delete calls.
   */
  custom?: {
    list: () => Promise<{ id: string; name: string }[]>;
    restore: (id: string) => Promise<unknown>;
    destroy: (id: string) => Promise<unknown>;
  };
  /** Who sees this kind at all. */
  visibleTo?: 'owner' | 'editor';
}

/**
 * Workspace-level records. People are "trashed" (a stage before deletion) and
 * clients are archived; both belong to the workspace, not to one project, and
 * both are managed by an owner or a planner-level role respectively (the API
 * enforces it; hiding them here just avoids buttons that would be refused).
 */
const WORKSPACE_KINDS: TrashKind[] = [
  {
    key: 'users',
    label: 'People',
    path: 'users',
    listKey: 'data',
    nameOf: r => r.name || r.email || 'Person with no e-mail',
    idOf: r => r.id,
    permanent: true,
    visibleTo: 'owner',
    custom: {
      list: async () => {
        const res = await apiGet<{ data?: { id: string; name: string | null; email: string | null }[] }>(planningPath('/users?status=trashed'));
        return (res?.data ?? []).map(u => ({ id: u.id, name: u.name || u.email || 'Person with no e-mail' }));
      },
      restore: id => apiPatch(planningPath(`/users/${encodeURIComponent(id)}/restore`), {}),
      destroy: id => apiDelete(planningPath(`/users/${encodeURIComponent(id)}/permanent`)),
    },
  },
  {
    key: 'clients',
    label: 'Clients',
    path: 'clients',
    listKey: 'data',
    nameOf: r => r.name,
    idOf: r => r.id,
    permanent: true,
    visibleTo: 'editor',
    custom: {
      list: async () => (await loadClients('', 'archived')).map(c => ({ id: c.id, name: c.name })),
      restore: id => restoreClient(id),
      destroy: id => deleteClient(id, true),
    },
  },
];

const KINDS: TrashKind[] = [
  { key: 'vendors', label: 'Vendors', path: 'vendors', listKey: 'vendors', nameOf: r => r.name, idOf: r => r.vendor_id ?? r.id, permanent: true },
  { key: 'guests', label: 'Guests', path: 'guests', listKey: 'guests', nameOf: r => r.name, idOf: r => r.id },
  { key: 'tasks', label: 'Tasks', path: 'tasks', listKey: 'tasks', nameOf: r => r.task ?? r.title, idOf: r => r.id },
  {
    key: 'events',
    label: 'Events',
    path: 'events',
    listKey: 'events',
    nameOf: r => r.title ?? r.name ?? `Event ${shortRef(r.id)}`,
    idOf: r => r.id,
    permanent: true,
  },
  {
    key: 'guidelines',
    label: 'Guidelines',
    path: 'guidelines',
    listKey: 'guidelines',
    nameOf: r => r.title ?? `Guideline ${shortRef(r.id)}`,
    idOf: r => r.id,
    permanent: true,
  },
  {
    key: 'fabrics',
    label: 'Fabrics',
    // Fabrics already have their own in-catalogue archived-view + restore
    // (FabricCatalog.tsx), but until now had no way to permanently delete
    // one short of editing the DB — deleteFabric() existed at the service
    // layer (AsoebiService.ts) but nothing in the UI ever called it. Listing
    // fabrics here closes that gap the same way Trash already does for
    // vendors/products.
    path: 'asoebi/fabrics',
    listKey: 'fabrics',
    nameOf: r => r.name ?? `Fabric ${shortRef(r.id)}`,
    idOf: r => r.id,
    permanent: true,
  },
  { key: 'products', label: 'Products', path: 'products', listKey: 'products', nameOf: r => r.name, idOf: r => r.id, permanent: true },
  { key: 'venues', label: 'Venues', path: 'venues', listKey: 'venues', nameOf: r => r.name, idOf: r => r.id },
  {
    key: 'vendor-bookings',
    label: 'Vendor Bookings',
    path: 'vendor-bookings',
    listKey: 'bookings',
    // A booking has no name of its own — it is identified by its reference and
    // the vendor it belongs to, and the vendor id here is the slug.
    nameOf: r => `${recordId('VB', r.id)} · ${shortRef(r.vendor_id)}`,
    idOf: r => r.id,
    permanent: true,
    restoreViaQuery: true,
  },
  {
    key: 'procurement',
    label: 'Procurement Orders',
    path: 'procurement/orders',
    // Procurement uses the envelope shape ({success, data, meta}) rather
    // than a named collection key like every other list endpoint here.
    listKey: 'data',
    nameOf: r => r.reference ?? r.supplier_name ?? `Order ${shortRef(r.id)}`,
    idOf: r => r.id,
    permanent: true,
  },
];

interface TrashRow {
  kind: TrashKind;
  id: string;
  name: string;
}

export default function TrashPanel() {
  const { showSuccess, showError } = useNotifications();
  const { isOwner, canEdit } = useRole();
  const [rows, setRows] = useState<TrashRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const visible = [
      ...KINDS,
      ...WORKSPACE_KINDS.filter(k => (k.visibleTo === 'owner' ? isOwner : canEdit)),
    ];
    const results = await Promise.all(
      visible.map(async kind => {
        try {
          if (kind.custom) {
            const items = await kind.custom.list();
            return items.map(i => ({ kind, id: i.id, name: i.name || '(untitled)' }));
          }
          const res = await apiGet<Record<string, any[]>>(
            planningPath(`/${kind.path}?archived=true`)
          );
          const list = res?.[kind.listKey] ?? [];
          return list.map(r => ({ kind, id: String(kind.idOf(r)), name: kind.nameOf(r) || '(untitled)' }));
        } catch {
          // One entity failing shouldn't blank the whole view.
          return [] as TrashRow[];
        }
      })
    );
    setRows(results.flat());
    setLoading(false);
  }, [isOwner, canEdit]);

  useEffect(() => { void load(); }, [load]);

  const restore = async (row: TrashRow) => {
    setBusy(`${row.kind.key}:${row.id}`);
    try {
      if (row.kind.custom) {
        await row.kind.custom.restore(row.id);
      } else if (row.kind.restoreViaQuery) {
        await apiDelete(planningPath(`/${row.kind.path}/${row.id}?restore=true`));
      } else {
        await apiPut(planningPath(`/${row.kind.path}/${row.id}`), { archived: false });
      }
      showSuccess(`Restored “${row.name}”.`);
      await load();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'record', action: 'restore' }));
    }
    setBusy(null);
  };

  // Everything in the Trash is archived, so Delete forever is allowed here.
  // The dialog stays open with the reason if the server refuses.
  const forever = useDeleteConfirm<TrashRow>({
    record: 'record',
    perform: row => row.kind.custom
      ? row.kind.custom.destroy(row.id)
      : apiDelete(planningPath(`/${row.kind.path}/${row.id}?permanent=true`)),
    onDone: row => { showSuccess(`Deleted “${row.name}”.`); void load(); },
  });
  const destroy = (row: TrashRow) => forever.request(row);

  const columns: Column<TrashRow>[] = [
    { key: 'name', header: 'Item', render: (_v, r) => <strong>{r.name}</strong> },
    { key: 'kind', header: 'Type', sortable: true, render: (_v, r) => r.kind.label },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (_v, r) => {
        const isBusy = busy === `${r.kind.key}:${r.id}`;
        return (
          <div className="trash-row-actions">
            <button
              type="button"
              className="secondary-btn"
              disabled={isBusy}
              onClick={() => { void restore(r); }}
            >
              <Icon name="refresh" size={14} /> Restore
            </button>
            {r.kind.permanent && (
              <button
                type="button"
                className="trash-delete-btn"
                disabled={isBusy}
                title="Delete forever"
                onClick={() => { void destroy(r); }}
              >
                <Icon name="trash" size={14} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="settings-section">
      {loading ? (
        <p className="settings-hint-text">Loading archived items…</p>
      ) : rows.length === 0 ? (
        <p className="settings-hint-text">Nothing archived — the trash is empty.</p>
      ) : (
        <DataTable columns={columns} data={rows} hoverable compact />
      )}
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this forever?"
        subject={forever.target ? `${forever.target.name} (${forever.target.kind.label})` : undefined}
        description="This removes the record for good and cannot be undone."
        confirmLabel="Delete forever"
      />
    </div>
  );
}
